import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { usePricing } from '../hooks/usePricing';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Star, UserCheck, Briefcase } from 'lucide-react';
import ConfirmHireDialog from './ConfirmHireDialog';
import ReviewModal from './ReviewModal';

/**
 * Dashboard "Action needed" card for the Services Marketplace hire→review loop (W3).
 * Fetches GET /api/pro-contacts/mine and surfaces two kinds of actionable rows:
 *   (a) as_client rows with prompt_24h_sent && hire_confirmed == null →
 *       "Did you hire {pro_name}?" via ConfirmHireDialog.
 *   (b) rows in EITHER group with forms_unlocked_at set && !my_review_submitted →
 *       "Write your review" via ReviewModal (context='pro_listing').
 *
 * to_user_id for the review: pro_id for as_client rows, client_id for as_pro rows.
 * (See report: as_client.pro_id is assumed present per the backend contract note.)
 *
 * Renders null when the flag is off or nothing is actionable.
 */
const PendingReviewsCard = ({ profile }) => {
  const { t } = useTranslation();
  const { pricing } = usePricing();
  const servicesEnabled = Boolean(pricing?.services_enabled);

  const [asClient, setAsClient] = useState([]);
  const [asPro, setAsPro] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hireTarget, setHireTarget] = useState(null);
  // reviewTarget: { toUserId, contextId, name }
  const [reviewTarget, setReviewTarget] = useState(null);

  const fetchMine = useCallback(async () => {
    if (!profile?.id) { setLoading(false); return; }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { setLoading(false); return; }
      const res = await api.get('/api/pro-contacts/mine', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setAsClient(res.data?.as_client || []);
      setAsPro(res.data?.as_pro || []);
    } catch (error) {
      console.error('[PendingReviewsCard] fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    if (servicesEnabled) {
      fetchMine();
    } else {
      setLoading(false);
    }
  }, [servicesEnabled, fetchMine]);

  // (a) Client rows awaiting a hire answer.
  const hirePrompts = asClient.filter(
    (r) => r.prompt_24h_sent && (r.hire_confirmed === null || r.hire_confirmed === undefined)
  );

  // (b) Rows (either side) where reviews are unlocked and not yet written.
  const clientReviews = asClient.filter((r) => r.forms_unlocked_at && !r.my_review_submitted);
  const proReviews = asPro.filter((r) => r.forms_unlocked_at && !r.my_review_submitted);

  const hasActionable = hirePrompts.length > 0 || clientReviews.length > 0 || proReviews.length > 0;

  // Flag off → render nothing.
  if (!servicesEnabled) return null;

  // Resolves to null in the common case → render nothing while loading too (no titled skeleton
  // that vanishes). The card simply appears when something is actionable.
  if (loading) return null;

  // Nothing to do → render nothing (keeps the dashboard clean).
  if (!hasActionable) return null;

  const openClientReview = (row) => {
    setReviewTarget({ toUserId: row.pro_id, contextId: row.listing_id, name: row.pro_name });
  };
  const openProReview = (row) => {
    setReviewTarget({ toUserId: row.client_id, contextId: row.listing_id, name: row.client_name });
  };

  return (
    <>
      <div data-testid="pending-reviews-card">
        <h2 className="font-heading text-2xl font-semibold mb-4">{t('services.pendingTitle')}</h2>
        <Card className="p-4 space-y-3">
          {/* Hire-confirmation prompts */}
          {hirePrompts.map((row) => (
            <div
              key={`hire-${row.id}`}
              className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-border p-3"
            >
              <div className="flex items-start gap-2 min-w-0">
                <UserCheck size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {t('services.hirePromptQuestion', { name: row.pro_name || t('services.thisPro') })}
                  </p>
                  <p className="text-xs text-muted-foreground truncate" title={row.listing_title}>
                    {row.listing_title}
                  </p>
                </div>
              </div>
              <Button size="sm" onClick={() => setHireTarget(row)} data-testid="open-confirm-hire-btn">
                {t('services.hirePromptAnswer')}
              </Button>
            </div>
          ))}

          {/* Client → Pro review prompts */}
          {clientReviews.map((row) => (
            <div
              key={`creview-${row.id}`}
              className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-border p-3"
            >
              <div className="flex items-start gap-2 min-w-0">
                <Star size={18} className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {t('services.reviewPromptClient', { name: row.pro_name || t('services.thisPro') })}
                  </p>
                  <p className="text-xs text-muted-foreground truncate" title={row.listing_title}>
                    {row.listing_title}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={!row.pro_id}
                onClick={() => openClientReview(row)}
                data-testid="open-client-review-btn"
              >
                {t('services.writeReview')}
              </Button>
            </div>
          ))}

          {/* Pro → Client review prompts */}
          {proReviews.map((row) => (
            <div
              key={`preview-${row.id}`}
              className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-border p-3"
            >
              <div className="flex items-start gap-2 min-w-0">
                <Briefcase size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {t('services.reviewPromptPro', { name: row.client_name || t('services.thisClient') })}
                  </p>
                  <p className="text-xs text-muted-foreground truncate" title={row.listing_title}>
                    {row.listing_title}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={!row.client_id}
                onClick={() => openProReview(row)}
                data-testid="open-pro-review-btn"
              >
                {t('services.writeReview')}
              </Button>
            </div>
          ))}
        </Card>
      </div>

      {hireTarget && (
        <ConfirmHireDialog
          contact={hireTarget}
          open={!!hireTarget}
          onOpenChange={(open) => { if (!open) setHireTarget(null); }}
          onDone={fetchMine}
        />
      )}

      <ReviewModal
        key={reviewTarget ? `${reviewTarget.contextId}-${reviewTarget.toUserId}` : 'idle'}
        open={!!reviewTarget}
        onOpenChange={(open) => { if (!open) setReviewTarget(null); }}
        targetUser={reviewTarget ? { id: reviewTarget.toUserId, name: reviewTarget.name } : null}
        context="pro_listing"
        contextId={reviewTarget?.contextId}
        onSuccess={fetchMine}
      />
    </>
  );
};

export default PendingReviewsCard;
